import { supabase } from '../config/supabase.config';
import { UserProfile, ProfileUpdateData } from '../types/settings.types';

export const settingsService = {
  async getProfile(userId: string): Promise<UserProfile> {
    console.log('🔍 Fetching profile for user:', userId);
    
    // First check if user is authenticated
    const authResponse = await supabase.auth.getUser();
    console.log('👤 Current authenticated user:', authResponse.data?.user?.id);
    
    // Fetch profile with organization name joined
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        organizations:organization_id (
          name
        )
      `)
      .eq('id', userId)
      .single();

    if (error) {
      console.error('❌ Error fetching profile:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      
      // If profile doesn't exist, try to create it
      if (error.code === 'PGRST116') {
        console.log('📝 Profile not found, attempting to create...');
        return await this.createProfile(userId);
      }
      
      throw error;
    }
    
    // Transform the response to include organization_name at top level
    const profile = {
      ...data,
      organization_name: data.organizations?.name || null,
      organizations: undefined // Remove nested object
    };
    
    console.log('✅ Profile fetched successfully:', profile);
    return profile as UserProfile;
  },

  async createProfile(userId: string): Promise<UserProfile> {
    console.log('🆕 Creating profile for user:', userId);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const newProfile = {
      id: userId,
      email: user.email!,
      full_name: user.user_metadata?.full_name || user.email,
      role: 'user' as const,
      is_active: true,
    };

    const { data, error } = await supabase
      .from('profiles')
      .insert(newProfile)
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating profile:', error);
      throw error;
    }

    console.log('✅ Profile created successfully:', data);
    return data;
  },

  async updateProfile(userId: string, updates: ProfileUpdateData): Promise<UserProfile> {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async uploadAvatar(userId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    return publicUrl;
  },

  async changePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
  },

  async deleteAvatar(avatarUrl: string): Promise<void> {
    const path = avatarUrl.split('/avatars/')[1];
    if (path) {
      await supabase.storage.from('avatars').remove([`avatars/${path}`]);
    }
  },
};